/**
 * image-compressor / app.js — the page.
 *
 * Everything expensive runs in a pool of workers; this file owns the DOM, the
 * queue and the reporting. Three UX decisions here are deliberate and worth
 * stating, because they are what the tool is FOR:
 *
 * 1. NO QUALITY SLIDER. The default is a goal ("visually identical"), not a
 *    number, and the number that came out is REPORTED afterwards. A slider asks
 *    the user to predict something only measurement can answer.
 *
 * 2. THE REPORT NAMES WHAT WAS DONE. Format, quality, subsampling, whether a
 *    palette was used, how many bytes of metadata went, and the four measured
 *    quality terms. "Optimised, -68%" is not checkable; "JPEG q82, 4:2:0,
 *    worst-region 0.94, no banding" is.
 *
 * 3. THE COMPARISON SHOWS THE WORST PART. Not the centre of the picture — the
 *    region the search itself found hardest. A before/after that opens on the
 *    easiest part of the image is a sales tool, not an inspection tool.
 */

(function () {
'use strict';

if (!window.__IC_CFG) return;
const CFG = window.__IC_CFG;
const T = CFG.t || {};
const SLUG = 'image-compressor';
const $ = (id) => document.getElementById(id);

const fmtBytes = (n) => {
    if (n === null || n === undefined) return '—';
    if (n < 1024) return n + ' B';
    if (n < 1024 * 1024) return (n / 1024).toFixed(n < 10240 ? 1 : 0) + ' KB';
    return (n / 1048576).toFixed(2) + ' MB';
};
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/* ── worker pool ─────────────────────────────────────────────────────────── */

/**
 * Can the work go into a worker at all?
 *
 * A worker can only touch pixels through OffscreenCanvas. Safari shipped that in
 * 16.4, and measured on the WebKit engine it is simply absent — every file came
 * back "this browser lacks the required features". The tool was not degraded on
 * those iPhones, it was dead.
 *
 * So the same pipeline runs on the page when the worker route is closed. It is
 * slower and it competes with the UI, which is why it is the fallback and not
 * the default — but a slower tool is a tool, and an error message is not.
 */
const CAN_WORKER = typeof Worker === 'function' && typeof OffscreenCanvas === 'function';

let inPageReady = null;
function bootInPage() {
    if (inPageReady) return inPageReady;
    inPageReady = (async () => {
        for (const k of ['metric', 'analyze', 'search', 'plan', 'png', 'exif', 'avif', 'codecs', 'pipeline']) {
            if (!CFG.urls[k]) throw new Error('missing engine url: ' + k);
            await loadScript(CFG.urls[k]);
        }
        window.__IC_HEIC_URL = CFG.urls.heic;
        window.__IC_MOZJPEG_URL = CFG.urls.mozjpeg;
        window.__icPipeline.useEngines({
            metric: window.__icMetric, analyze: window.__icAnalyze, search: window.__icSearch,
            plan: window.__icPlan, exif: window.__icExif, codecs: window.__icCodecs,
        });
        try {
            window.__ae && window.__ae('feature_use', { slug: SLUG, action: 'in_page_fallback' });
        } catch (e) {}
    })();
    return inPageReady;
}

/* ── telemetry ───────────────────────────────────────────────────────────────
 *
 * Every event below exists to answer a question we will actually act on. An
 * event nobody has a decision for is noise, and noise is what makes a dashboard
 * unreadable later.
 *
 *   ic_open        what the DEVICE can do → how many people fall to the in-page
 *                  lane. This is the iPhone-reach number; without it the
 *                  OffscreenCanvas gap would have stayed invisible in the wild
 *                  exactly as it was invisible in the suite.
 *   ic_queue       batch size and the formats people really drop → is the cap of
 *                  20 files/day the right unit, and is HEIC worth its 1.4 MB?
 *   ic_settings    mode / gate / format / resize → the byte-target lane is a
 *                  measured demand cluster; this says whether it is used.
 *   ic_file        per file: kind, source→output format, saving, whether the
 *                  original was KEPT, and how long it took. "Kept" is the one to
 *                  watch: it is correct behaviour that reads like a failure.
 *   ic_action      compare / diff / download / zip → is the differentiator used,
 *                  and do people accept a format change (WebP) by downloading it?
 *   tool_error     failure codes, so the tail is a list and not a feeling.
 */
const TM = {
    send(action, data) {
        try {
            if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, action }, data || {}));
        } catch (e) {}
    },
    stage(name) { try { window.__stage && window.__stage(name); } catch (e) {} },
    fail(code, data) {
        try {
            if (window.__aeDiagnostic) window.__aeDiagnostic(Object.assign({ slug: SLUG, issue: code }, data || {}));
            else if (window.__ae) window.__ae('tool_error', Object.assign({ slug: SLUG, error_type: code }, data || {}));
        } catch (e) {}
    },
};

const HW = navigator.hardwareConcurrency || 4;
// Leave a core for the page. Four is plenty: each worker holds several full
// decoded copies of a photo, and eight of them on a 12 MP batch is how a tab
// gets killed for memory rather than finishing faster.
const POOL_SIZE = Math.max(1, Math.min(4, HW - 1));

class Pool {
    constructor() { this.workers = []; this.idle = []; this.queue = []; this.jobs = new Map(); }
    spawn() {
        const w = new Worker(CFG.workerUrl);
        w.postMessage({ type: 'init', urls: CFG.urls });
        w.onmessage = (e) => this.onMessage(w, e.data);
        w.onerror = () => { /* surfaced through the job's own error path */ };
        this.workers.push(w);
        return w;
    }
    onMessage(w, msg) {
        if (msg.type === 'ready') return;
        const job = this.jobs.get(msg.id);
        if (!job) return;
        if (msg.type === 'progress') { job.onProgress && job.onProgress(msg.pct, msg.stage); return; }
        if (msg.type === 'stage') { job.onStage && job.onStage(msg.stage, msg.format); return; }
        this.jobs.delete(msg.id);
        this.idle.push(w);
        if (msg.type === 'done') job.resolve(msg);
        else job.reject(Object.assign(new Error(msg.message || 'failed'), { code: msg.code }));
        this.pump();
    }
    pump() {
        while (this.queue.length && (this.idle.length || this.workers.length < POOL_SIZE)) {
            const w = this.idle.pop() || this.spawn();
            const job = this.queue.shift();
            this.jobs.set(job.id, job);
            w.postMessage({ type: 'run', id: job.id, blob: job.blob, intent: job.intent, urls: CFG.urls });
        }
    }
    run(id, blob, intent, onStage, onProgress) {
        if (!CAN_WORKER) return this.runInPage(id, blob, intent, onStage, onProgress);
        return new Promise((resolve, reject) => {
            this.queue.push({ id, blob, intent, onStage, onProgress, resolve, reject });
            this.pump();
        });
    }
    /** Same pipeline, on the main thread. One file at a time — parallelism here
     *  would only fight the UI for the single thread it is already blocking. */
    async runInPage(id, blob, intent, onStage, onProgress) {
        await bootInPage();
        this.inPageCancel = this.inPageCancel || new Set();
        return new Promise((resolve, reject) => {
            window.__icPipeline.run({ id, blob, intent }, {
                isCancelled: () => this.inPageCancel.has(id),
                post: (payload) => {
                    if (payload.type === 'progress') { onProgress && onProgress(payload.pct, payload.stage); return; }
                    if (payload.type === 'stage') { onStage && onStage(payload.stage, payload.format); return; }
                    if (payload.type === 'done') resolve(payload);
                },
            }).catch(reject);
        });
    }
    cancelAll() {
        this.queue.length = 0;
        if (this.inPageCancel) for (const it of items) this.inPageCancel.add(it.id);
        for (const w of this.workers) { try { w.terminate(); } catch (e) {} }
        this.workers = []; this.idle = []; this.jobs.clear();
    }
}
const pool = new Pool();

/* ── state ───────────────────────────────────────────────────────────────── */

let items = [];          // {id, file, status, report, bytes, mime, ext, url}
let running = false;
let seq = 0;

function intentFromUI() {
    const mode = document.querySelector('input[name="icMode"]:checked');
    const isTarget = mode && mode.value === 'target';
    const targetRaw = $('icTargetInput') ? $('icTargetInput').value : '';
    const resizeSel = $('icResize') ? $('icResize').value : 'keep';
    let resize = null;
    if (resizeSel === 'custom') {
        const px = parseInt($('icResizePx').value, 10);
        if (px > 0) resize = { longSide: px };
    } else if (resizeSel.indexOf('pct:') === 0) {
        resize = { percent: parseFloat(resizeSel.slice(4)) };
    }
    return {
        gate: $('icGate') ? $('icGate').value : 'identical',
        targetBytes: isTarget ? parseTargetText(targetRaw) : null,
        format: $('icFormat') ? $('icFormat').value : 'auto',
        purpose: (resizeSel !== 'keep' && resizeSel !== 'custom') ? resizeSel : 'keep',
        resize,
        trimBorders: $('icTrim') ? $('icTrim').checked : false,
        keepMetadata: $('icMeta') ? $('icMeta').checked : false,
        // Whether the size box means "each file" or "all of them together".
        targetScope: $('icTargetScope') ? $('icTargetScope').value : 'each',
    };
}

// Same grammar as the engine's parseTarget; duplicated here only so the field
// can validate as the user types, without waking a worker.
function parseTargetText(text) {
    if (!text) return null;
    const s = String(text).trim().toLowerCase().replace(',', '.');
    const m = s.match(/^([0-9]*\.?[0-9]+)\s*(гб|gb|мб|mb|m|кб|kb|k|б|b)?$/i);
    if (!m) return null;
    const n = parseFloat(m[1]);
    if (!isFinite(n) || n <= 0) return null;
    const u = m[2] || 'kb';
    if (/^(гб|gb)$/.test(u)) return Math.round(n * 1073741824);
    if (/^(мб|mb|m)$/.test(u)) return Math.round(n * 1048576);
    if (/^(б|b)$/.test(u)) return Math.round(n);
    return Math.round(n * 1024);
}

/* ── rendering ───────────────────────────────────────────────────────────── */

function outName(item) {
    const base = item.file.name.replace(/\.[^.]+$/, '');
    /* The EXTENSION always follows the real bytes even when the name is kept:
     * a WebP called .jpg is a broken file, and "keep the name" is a request
     * about the name, not about lying to the next program that opens it. */
    const keep = $('icKeepName') && $('icKeepName').checked;
    return base + (keep ? '' : '-min') + '.' + (item.ext || 'jpg');
}

function qualityBadge(rep) {
    if (!rep) return '';
    if (rep.lossless) return `<span class="ic-badge ic-badge-ok">${esc(T.lossless || 'без потерь')}</span>`;
    const m = rep.metrics;
    if (!m) return '';
    const gate = m.bestGate;
    const label = gate === 'identical' ? (T.gate_identical_short || 'визуально идентично')
        : gate === 'high' ? (T.gate_high_short || 'высокое')
        : gate === 'balanced' ? (T.gate_balanced_short || 'сбалансировано')
        : (T.gate_small_short || 'заметно');
    const cls = gate === 'identical' ? 'ok' : gate === 'high' ? 'ok' : gate === 'balanced' ? 'mid' : 'low';
    return `<span class="ic-badge ic-badge-${cls}" title="${esc(T.score_hint || '')}">${esc(label)} · ${m.score}</span>`;
}

function detailLine(rep, kept) {
    if (!rep) return '';
    if (kept) {
        // Nothing was re-encoded, so quoting a quality and a subsampling here
        // describes a file that was thrown away. Measured on a 1x1 PNG: the row
        // read "PNG q35 4:2:0" for bytes that were never touched.
        const px = rep.width && rep.height ? (rep.width + '×' + rep.height) : '';
        return [String(rep.sourceFormat || '').toUpperCase(), px,
                (T.kept_original || 'файл не изменён')].filter(Boolean).join(' · ');
    }
    const bits = [];
    bits.push(String(rep.format || '').toUpperCase());
    if (rep.quality) bits.push('q' + rep.quality);
    if (rep.subsampling && rep.subsampling !== '-') bits.push(rep.subsampling);
    if (rep.detail) bits.push(rep.detail);
    if (rep.outWidth && (rep.outWidth !== rep.width || rep.outHeight !== rep.height)) {
        bits.push(rep.width + '×' + rep.height + ' → ' + rep.outWidth + '×' + rep.outHeight);
    } else if (rep.outWidth) {
        bits.push(rep.outWidth + '×' + rep.outHeight);
    }
    if (rep.metadataBytes > 2048 && !rep.metadataKept) {
        bits.push((T.meta_dropped || 'метаданные −') + fmtBytes(rep.metadataBytes));
    }
    return bits.join(' · ');
}

function markStale() {
    let any = false;
    for (const it of items) if (it.status === 'done') { it.stale = true; any = true; }
    if (any) {
        const btn = $('icRunBtn');
        if (btn) btn.textContent = T.btn_rerun || 'Пересжать';
        render();
    }
}

function showTargetError(msg) {
    const el = $('icTargetErr');
    if (el) { el.textContent = msg; el.hidden = false; }
}
function clearTargetError() {
    const el = $('icTargetErr');
    if (el) { el.textContent = ''; el.hidden = true; }
}

function render() {
    const list = $('icList');
    if (!list) return;
    if (!items.length) { list.innerHTML = ''; $('icResults').hidden = true; return; }
    $('icResults').hidden = false;

    list.innerHTML = items.map((it) => {
        const rep = it.report;
        const pct = rep ? rep.savedPct : null;
        const bar = rep ? Math.max(2, Math.min(100, Math.round((rep.outputBytes / Math.max(1, rep.originalBytes)) * 100))) : 100;
        let right = '';
        if (it.status === 'done' && rep) {
            const missed = rep.targetMet === false;
            right = `
              <div class="ic-sizes">
                <span class="ic-old">${fmtBytes(rep.originalBytes)}</span>
                <span class="ic-arrow">→</span>
                <span class="ic-new">${fmtBytes(rep.outputBytes)}</span>
                <span class="ic-pct ${missed ? 'miss' : (pct > 0 ? 'good' : 'zero')}">${pct > 0 ? '−' + pct + '%' : (T.no_gain || 'уже оптимально')}</span>
                ${missed ? `<span class="ic-miss">${esc(T.target_missed || 'меньше не получается')}</span>` : ''}
              </div>
              <div class="ic-meter"><i style="width:${bar}%"></i></div>
              <div class="ic-detail">${esc(detailLine(rep, it.kept))}</div>`;
        } else if (it.status === 'error') {
            const gifHint = it.errCode === 'ANIMATED'
                ? ` <a class="ic-link" href="/${CFG.locale || 'ru'}/gif-compressor">${esc(T.err_animated_link || 'сжать анимацию')}</a>` : '';
            right = `<div class="ic-err">${esc(it.error || T.err_generic || 'не удалось')}${gifHint ? '' : ''}</div>${gifHint ? `<div class="ic-detail">${gifHint}</div>` : ''}`;
        } else if (it.status === 'working') {
            const pct = typeof it.pct === 'number' ? it.pct : null;
            right = `<div class="ic-working"><span class="ic-spin"></span>${esc(it.stage || T.working || 'обработка…')}${pct !== null ? ' · ' + pct + '%' : ''}</div>${pct !== null ? `<div class="ic-meter ic-meter-live"><i style="width:${pct}%"></i></div>` : ''}`;
        } else {
            right = `<div class="ic-detail">${fmtBytes(it.file.size)}</div>`;
        }
        return `
        <div class="ic-row${it.stale ? ' ic-row-stale' : ''}" data-id="${it.id}">
          <div class="ic-thumb">${it.thumb ? `<img src="${it.thumb}" alt="">` : ''}</div>
          <div class="ic-info">
            <div class="ic-name" title="${esc(it.file.name)}">${esc(it.file.name)}${it.stale ? ` <span class="ic-stale-tag">${esc(T.stale || 'настройки изменились')}</span>` : ''}</div>
            ${right}
          </div>
          <div class="ic-actions">
            ${qualityBadge(rep)}
            ${it.status === 'done' || it.status === 'queued' || it.status === 'error'
                ? `<button type="button" class="ic-mini${it.override ? ' ic-mini-on' : ''}" data-act="tune" data-id="${it.id}" title="${esc(T.tune || 'Настроить этот файл')}">⚙</button>` : ''}
            ${it.errCode === 'ANIMATED' ? `<button type="button" class="ic-mini" data-act="flatten" data-id="${it.id}">${esc(T.flatten_anyway || 'Первый кадр')}</button>` : ''}
            ${it.status === 'done' ? `<button type="button" class="ic-mini" data-act="compare" data-id="${it.id}">${esc(T.compare || 'Сравнить')}</button>` : ''}
            ${it.status === 'done' ? `<button type="button" class="ic-mini ic-mini-primary" data-act="save" data-id="${it.id}">${esc(T.save || 'Скачать')}</button>` : ''}
            <button type="button" class="ic-mini ic-mini-x" data-act="remove" data-id="${it.id}" aria-label="${esc(T.remove || 'Убрать')}">×</button>
          </div>
        </div>`;
    }).join('');

    const done = items.filter(i => i.status === 'done');
    const totalIn = done.reduce((a, i) => a + i.report.originalBytes, 0);
    const totalOut = done.reduce((a, i) => a + i.report.outputBytes, 0);
    const sum = $('icSummary');
    if (sum) {
        if (done.length > 1) {
            sum.hidden = false;
            const savedPct = totalIn ? Math.round((1 - totalOut / totalIn) * 100) : 0;
            const budget = (document.querySelector('input[name="icMode"]:checked') || {}).value === 'target' &&
                $('icTargetScope') && $('icTargetScope').value === 'total'
                ? parseTargetText($('icTargetInput').value) : null;
            const fits = budget ? totalOut <= budget : null;
            sum.innerHTML = `<strong>${done.length}</strong> ${esc(T.files_done || 'файлов')} · ${fmtBytes(totalIn)} → <strong>${fmtBytes(totalOut)}</strong> · <span class="ic-pct good">−${savedPct}%</span>${budget ? ` · <span class="ic-pct ${fits ? 'good' : 'miss'}">${esc(T.of_budget || 'из')} ${fmtBytes(budget)}</span>` : ''}`;
        } else sum.hidden = true;
    }
    $('icZipBtn').hidden = done.length < 2;
}

/* ── running ─────────────────────────────────────────────────────────────── */

async function processAll() {
    if (running) return;
    const pending = items.filter(i => i.status === 'queued' || i.status === 'error' || i.stale);
    if (!pending.length) return;

    /* Asking for a SIZE and silently getting a QUALITY result instead is the
     * worst kind of bug, because it looks like success. Measured in the UX
     * pass: an empty field and the word "абвгд" both produced a cheerful -48%
     * and not one word about the size that was asked for. */
    const modeEl = document.querySelector('input[name="icMode"]:checked');
    if (modeEl && modeEl.value === 'target' && !parseTargetText($('icTargetInput').value)) {
        showTargetError(T.err_target_bad || 'Укажите размер: например, 200 КБ или 1 МБ');
        $('icTargetInput').focus();
        TM.send('ic_settings_rejected', { reason: 'unparseable_target' });
        return;
    }
    clearTargetError();

    const TL = window.__toolLimit;
    let tlRun = null;
    if (TL) {
        tlRun = TL.begin(SLUG, TL.fileKeys(pending.map(i => i.file)));
        if (!tlRun) return;              // the wall is up; the module shows it
    }

    running = true;
    $('icRunBtn').disabled = true;
    $('icCancelBtn').hidden = false;
    const intent = intentFromUI();

    /* ONE budget for the whole batch. Allocated in proportion to the input
     * sizes, because that is the split that keeps every picture at roughly the
     * same quality — an equal split would gut the big photo to pay for the
     * thumbnail. Each file then hits its own share from below, so the sum
     * cannot exceed the budget by construction. */
    const shares = new Map();
    if (intent.targetBytes && intent.targetScope === 'total') {
        const totalIn = pending.reduce((a, i) => a + i.file.size, 0) || 1;
        for (const it of pending) {
            shares.set(it.id, Math.max(6 * 1024, Math.round(intent.targetBytes * (it.file.size / totalIn))));
        }
    }

    TM.stage('processing');
    TM.send('ic_settings', {
        mode: intent.targetBytes ? 'target' : 'gate',
        gate: intent.gate,
        target_kb: intent.targetBytes ? Math.round(intent.targetBytes / 1024) : null,
        format: intent.format,
        purpose: intent.purpose,
        custom_long_side: intent.resize ? intent.resize.longSide : null,
        trim: intent.trimBorders, keep_meta: intent.keepMetadata,
        files: pending.length,
    });

    const okFiles = [];
    await Promise.all(pending.map(async (it) => {
        it.status = 'working'; it.stage = ''; it.stale = false; it.pct = 0; it.t0 = performance.now(); render();
        try {
            const perFile = Object.assign({}, intent, it.override || {});
            if (shares.has(it.id)) perFile.targetBytes = shares.get(it.id);
            if (it.flatten) perFile.allowFlatten = true;
            const res = await pool.run(it.id, it.file, perFile, (stage) => {
                it.stage = T['stage_' + stage] || stage;
                render();
            }, (pct) => {
                it.pct = pct;
                render();
            });
            it.status = 'done';
            it.report = res.report;
            TM.send('ic_file', {
                kind: res.report.kind,
                src: res.report.sourceFormat, out: res.report.format,
                kept: !!res.kept, kept_reason: res.keptReason || null,
                saved_pct: res.report.savedPct,
                in_kb: Math.round(res.report.originalBytes / 1024),
                out_kb: Math.round(res.report.outputBytes / 1024),
                mp: +((res.report.width * res.report.height) / 1e6).toFixed(1),
                quality: res.report.quality, lossless: !!res.report.lossless,
                gate_met: res.report.gateMet !== false, target_met: res.report.targetMet,
                score: res.report.metrics ? res.report.metrics.score : null,
                ms: Math.round(performance.now() - it.t0),
                lane: CAN_WORKER ? 'worker' : 'page',
            });
            it.bytes = res.bytes;
            it.mime = res.mime;
            it.ext = res.ext;
            it.kept = res.kept;
            if (it.url) URL.revokeObjectURL(it.url);
            it.url = URL.createObjectURL(new Blob([res.bytes], { type: res.mime }));
            okFiles.push(it.file);
        } catch (e) {
            it.status = 'error';
            it.error = errText(e);
            it.errCode = (e && e.code) || 'FAILED';
            TM.fail((e && e.code) || 'FAILED', {
                ext: (it.file.name.split('.').pop() || '?').toLowerCase(),
                size_kb: Math.round(it.file.size / 1024),
                lane: CAN_WORKER ? 'worker' : 'page',
            });
        }
        render();
    }));

    running = false;
    $('icRunBtn').disabled = false;
    $('icCancelBtn').hidden = true;
    if (!items.some(i => i.stale)) $('icRunBtn').textContent = T.btn_run || 'Сжать';
    render();

    // Bill only what was actually delivered — a file that failed must not eat a
    // slot. `begin()` reserved the batch; `record()` settles it.
    if (tlRun) {
        try {
            if (okFiles.length) tlRun.commit(TL.fileKeys(okFiles));
            else tlRun.abort();
        } catch (e) { try { tlRun.abort(); } catch (_) {} }
    }
    try {
        const done = items.filter(i => i.status === 'done');
        if (done.length) {
            window.__aeToolComplete && window.__aeToolComplete({
                files: done.length,
                saved_pct: Math.round((1 - done.reduce((a, i) => a + i.report.outputBytes, 0) /
                    Math.max(1, done.reduce((a, i) => a + i.report.originalBytes, 0))) * 100),
                mode: intent.targetBytes ? 'target' : 'gate',
                gate: intent.gate,
            });
        }
    } catch (e) {}
}

function errText(e) {
    const code = e && e.code;
    if (code === 'ANIMATED') return T.err_animated || 'это анимация';
    if (code === 'VENDOR_LOAD_FAILED') return T.err_vendor || 'не удалось загрузить компонент — обновите страницу';
    if (code === 'FORMAT_UNSUPPORTED') return T.err_format || 'формат не поддерживается';
    if (code === 'DECODE_EMPTY') return T.err_decode || 'не удалось прочитать изображение';
    if (code === 'NO_CANVAS' || code === 'NO_COMPRESSION_STREAM') return T.err_browser || 'браузер не поддерживает нужные возможности';
    return (e && e.message) || T.err_generic || 'не удалось';
}

/* ── comparison ──────────────────────────────────────────────────────────── */

let cmp = { item: null, zoom: 1, ox: 0, oy: 0, diff: false };

async function openCompare(item) {
    if (!item || item.status !== 'done') return;
    cmp.item = item; cmp.zoom = 1; cmp.ox = 0; cmp.oy = 0; cmp.diff = false;
    const dlg = $('icCmpDlg');
    $('icCmpTitle').textContent = item.file.name;
    $('icCmpMeta').innerHTML = `${fmtBytes(item.report.originalBytes)} → <strong>${fmtBytes(item.report.outputBytes)}</strong> · ${esc(detailLine(item.report, item.kept))}`;
    /* Open FIRST, then draw. A closed <dialog> has no layout, so measuring the
     * canvas's parent before showModal() returns a collapsed width and the
     * picture lands in a corner of a full-width stage — which is what the first
     * screenshot pass showed. */
    if (!dlg.open) dlg.showModal();
    await drawCompare();
}

/* A bitmap with the photo's EXIF rotation applied. WebKit learned the value
 * 'from-image' only in 266893@main (14.08.2023, Safari 17); before it the enum
 * was 'none' | 'flipY', and an unknown value throws a bare TypeError («Type
 * error»). On iOS 16 that killed the comparison dialog and the thumbnails
 * (Sentry TIMBRICA-FRONTEND-P3/P4, Yandex 25.12 on iOS 16.7) while compression
 * itself worked — codecs.js decode() already retried without the option, these
 * call sites did not. Retrying bare is not a downgrade there: old WebKit's
 * default 'none' MEANT «as the file says». */
async function orientedBitmap(src) {
    try { return await createImageBitmap(src, { imageOrientation: 'from-image' }); }
    catch (e) { return createImageBitmap(src); }
}

/* Keep the stage right when the window changes shape — including the phone
 * rotating, which is otherwise a permanently wrong-sized canvas. */
let cmpResizeTimer = null;
window.addEventListener('resize', () => {
    if (!cmp.item || !$('icCmpDlg').open) return;
    clearTimeout(cmpResizeTimer);
    cmpResizeTimer = setTimeout(drawCompare, 120);
});

async function loadBitmaps(item) {
    if (item._bmps) return item._bmps;
    const a = await orientedBitmap(item.file);
    const b = await createImageBitmap(new Blob([item.bytes], { type: item.mime }));
    item._bmps = { a, b };
    return item._bmps;
}

/* The comparison is drawn from handlers that never await it — the Compare
 * click, the split slider, zoom, the diff toggle, resize — so a failure inside
 * used to become an unhandled rejection and leave an OPEN, EMPTY dialog (P3:
 * the click; P4: zoom pressed on that empty dialog). Now it fails once, in
 * words, and does not leave the empty stage behind. */
async function drawCompare() {
    try {
        await paintCompare();
    } catch (err) {
        const dlg = $('icCmpDlg');
        if (dlg && dlg.open) dlg.close();
        cmp.item = null;
        if (window.__handleError) window.__handleError(err, 'ic_compare', { say: T.err_decode || T.err_generic || '' });
    }
}

async function paintCompare() {
    const item = cmp.item;
    if (!item) return;
    const { a, b } = await loadBitmaps(item);
    const cv = $('icCmpCanvas');
    const box = cv.parentElement.getBoundingClientRect();
    const W = Math.max(320, Math.floor(box.width));
    const H = Math.max(240, Math.floor(Math.min(window.innerHeight * 0.55, box.width * 0.62)));
    cv.width = W * (window.devicePixelRatio || 1);
    cv.height = H * (window.devicePixelRatio || 1);
    cv.style.width = W + 'px'; cv.style.height = H + 'px';
    const ctx = cv.getContext('2d');
    ctx.setTransform(window.devicePixelRatio || 1, 0, 0, window.devicePixelRatio || 1, 0, 0);
    ctx.clearRect(0, 0, W, H);

    // Open on the region the search itself found hardest, not on the middle.
    const rep = item.report;
    const fit = Math.min(W / a.width, H / a.height);
    const scale = fit * cmp.zoom;
    const dw = a.width * scale, dh = a.height * scale;
    let dx = (W - dw) / 2 + cmp.ox, dy = (H - dh) / 2 + cmp.oy;

    ctx.imageSmoothingEnabled = cmp.zoom < 3;
    const split = Math.round(W * (parseInt($('icCmpSplit').value, 10) / 100));

    if (cmp.diff) {
        ctx.drawImage(b, dx, dy, dw, dh);
        ctx.save();
        ctx.globalCompositeOperation = 'difference';
        ctx.drawImage(a, dx, dy, dw, dh);
        ctx.restore();
        // Amplify: a real difference map is nearly black and reads as "nothing
        // changed", which is exactly the wrong impression to leave.
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        for (let i = 0; i < 3; i++) ctx.drawImage(cv, 0, 0, W, H);
        ctx.restore();
    } else {
        ctx.save();
        ctx.beginPath(); ctx.rect(0, 0, split, H); ctx.clip();
        ctx.drawImage(a, dx, dy, dw, dh);
        ctx.restore();
        ctx.save();
        ctx.beginPath(); ctx.rect(split, 0, W - split, H); ctx.clip();
        ctx.drawImage(b, dx, dy, dw, dh);
        ctx.restore();
        ctx.fillStyle = 'rgba(255,255,255,.9)';
        ctx.fillRect(split - 1, 0, 2, H);
        ctx.font = '600 12px system-ui, sans-serif';
        ctx.fillStyle = 'rgba(0,0,0,.55)';
        ctx.fillRect(8, 8, 82, 20); ctx.fillRect(W - 90, 8, 82, 20);
        ctx.fillStyle = '#fff';
        ctx.fillText(T.before || 'Оригинал', 14, 22);
        ctx.fillText(T.after || 'Сжатый', W - 84, 22);
    }

    const m = rep.metrics;
    $('icCmpTerms').innerHTML = rep.lossless
        ? `<span class="ic-term ok">${esc(T.lossless_full || 'Пиксели не изменились')}</span>`
        : (m ? [
            term(T.term_structure || 'структура', m.ssim.toFixed(4), m.ssim >= 0.985),
            term(T.term_worst || 'худший участок', m.low.toFixed(3), m.low >= 0.915),
            term(T.term_colour || 'цвет', m.chroma.toFixed(1), m.chroma <= 8),
            term(T.term_banding || 'полосы', m.flat.toFixed(2), m.flat <= 1.2),
          ].join('') : '');
}
function term(label, value, ok) {
    return `<span class="ic-term ${ok ? 'ok' : 'warn'}"><b>${esc(label)}</b> ${esc(value)}</span>`;
}

/* ── per-file settings ───────────────────────────────────────────────────── */

/* One picture in a batch often needs different treatment — the screenshot among
 * the holiday photos, the one shot that must stay lossless. Re-running the whole
 * queue to change one file is the thing batch tools make people do. */
let tuneItem = null;

function openTune(item) {
    tuneItem = item;
    const dlg = $('icTuneDlg');
    $('icTuneName').textContent = item.file.name;
    const o = item.override || {};
    $('icTuneGate').value = o.gate || (($('icGate') && $('icGate').value) || 'identical');
    $('icTuneFormat').value = o.format || (($('icFormat') && $('icFormat').value) || 'auto');
    $('icTuneTarget').value = o.targetBytes ? Math.round(o.targetBytes / 1024) + ' КБ' : '';
    $('icTuneReset').hidden = !item.override;
    if (!dlg.open) dlg.showModal();
}

function applyTune(reset) {
    if (!tuneItem) return;
    if (reset) {
        delete tuneItem.override;
    } else {
        const t = parseTargetText($('icTuneTarget').value);
        tuneItem.override = {
            gate: $('icTuneGate').value,
            format: $('icTuneFormat').value,
            targetBytes: t || null,
        };
        // A per-file size overrides the batch split for this one file, which is
        // the only reading of "this file must be under X" that makes sense.
        if (t) tuneItem.override.targetScope = 'each';
    }
    TM.send('ic_action', { what: reset ? 'tune_reset' : 'tune_apply' });
    $('icTuneDlg').close();
    if (tuneItem.status === 'done') tuneItem.stale = true;
    render();
    processAll();
}

/* ── downloads ───────────────────────────────────────────────────────────── */

function saveOne(item) {
    if (!item || !item.bytes) return;
    window.__downloadFile(new Blob([item.bytes], { type: item.mime }), outName(item));
}

async function saveZip() {
    const done = items.filter(i => i.status === 'done');
    if (!done.length) return;
    const btn = $('icZipBtn');
    btn.disabled = true;
    try {
        if (!window.JSZip) await loadScript(CFG.urls.jszip);
        const zip = new window.JSZip();
        const used = new Set();
        for (const it of done) {
            let name = outName(it), i = 2;
            while (used.has(name)) name = name.replace(/(\.[^.]+)$/, '-' + (i++) + '$1');
            used.add(name);
            zip.file(name, it.bytes);
        }
        const blob = await zip.generateAsync({ type: 'blob', compression: 'STORE' });
        window.__downloadFile(blob, (T.zip_name || 'compressed-images') + '.zip');
    } catch (e) {
        window.__handleError(e, 'zip');
    } finally { btn.disabled = false; }
}

function loadScript(src) {
    return new Promise((res, rej) => {
        const s = document.createElement('script');
        s.src = src; s.onload = res; s.onerror = () => rej(new Error('load failed'));
        document.head.appendChild(s);
    });
}

/* ── wiring ──────────────────────────────────────────────────────────────── */

function addFiles(files) {
    for (const f of files) {
        if (!f || !f.size) continue;
        const it = { id: 'ic' + (++seq), file: f, status: 'queued', thumb: null };
        items.push(it);
        // Thumbnail from a downscaled bitmap, not from an object URL of the
        // original: a batch of 12 MP photos as <img src> is hundreds of MB of
        // decoded bitmaps held by the document.
        makeThumb(f).then((u) => { it.thumb = u; render(); }).catch(() => {});
    }
    render();
    if (window.__toolLimit) window.__toolLimit.precheck(SLUG, items.map(i => i.file));
    $('icRunBtn').disabled = !items.length;
    TM.send('ic_queue', {
        queued: items.length,
        added: files.length,
        exts: [...new Set(items.map(i => (i.file.name.split('.').pop() || '?').toLowerCase()))].slice(0, 8).join(','),
        total_mb: +(items.reduce((a, i) => a + i.file.size, 0) / 1048576).toFixed(1),
        biggest_mb: +(Math.max(0, ...items.map(i => i.file.size)) / 1048576).toFixed(1),
    });
}

async function makeThumb(file) {
    const bmp = await orientedBitmap(file);
    const S = 72 * (window.devicePixelRatio || 1);
    const k = Math.min(S / bmp.width, S / bmp.height, 1);
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(bmp.width * k));
    c.height = Math.max(1, Math.round(bmp.height * k));
    c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
    bmp.close && bmp.close();
    return c.toDataURL('image/jpeg', 0.7);
}

function boot() {
    const up = $('icUpload');
    if (up) {
        /* __bindUploaderFile, not a raw listener: on a slow network the per-tool
         * script can attach AFTER the user has already picked a file, and the
         * event is simply lost — the tool then sits dead until they re-pick.
         * The helper also replays a file that arrived early. (UploaderListenerRaceTest
         * flags the raw pattern.) */
        const onPicked = (file, detail) => {
            const many = detail && detail.files && detail.files.length ? Array.from(detail.files) : (file ? [file] : []);
            addFiles(many);
        };
        if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(up, onPicked);
        else up.addEventListener('tool-uploader:file', (e) => onPicked(e.detail && e.detail.file, e.detail));

        up.addEventListener('tool-uploader:clear', () => {
            items.forEach(i => i.url && URL.revokeObjectURL(i.url));
            items = []; render(); $('icRunBtn').disabled = true;
        });
    }

    $('icRunBtn').addEventListener('click', processAll);
    $('icCancelBtn').addEventListener('click', () => {
        pool.cancelAll(); running = false;
        items.forEach(i => { if (i.status === 'working') i.status = 'queued'; });
        $('icRunBtn').disabled = false; $('icCancelBtn').hidden = true; render();
    });
    $('icZipBtn').addEventListener('click', () => { TM.send('ic_action', { what: 'zip', files: items.filter(i => i.status === 'done').length }); saveZip(); });

    $('icList').addEventListener('click', (e) => {
        const b = e.target.closest('[data-act]');
        if (!b) return;
        const it = items.find(i => i.id === b.dataset.id);
        if (!it) return;
        if (b.dataset.act === 'save') { TM.send('ic_action', { what: 'save', out: it.report && it.report.format, changed_format: !!(it.report && it.report.format !== it.report.sourceFormat) }); saveOne(it); }
        else if (b.dataset.act === 'compare') { TM.send('ic_action', { what: 'compare', score: it.report && it.report.metrics ? it.report.metrics.score : null }); openCompare(it); }
        else if (b.dataset.act === 'flatten') {
            // An explicit, informed choice to keep only the first frame.
            TM.send('ic_action', { what: 'flatten_animation' });
            it.status = 'queued'; it.error = null; it.flatten = true; it.errCode = null;
            render();
            processAll();
        }
        else if (b.dataset.act === 'tune') { openTune(it); }
        else if (b.dataset.act === 'remove') {
            if (it.url) URL.revokeObjectURL(it.url);
            items = items.filter(i => i !== it);
            render();
            $('icRunBtn').disabled = !items.length;
            if (window.__toolLimit) window.__toolLimit.precheck(SLUG, items.map(i => i.file));
        }
    });

    // Mode switch: quality goal vs byte budget.
    document.querySelectorAll('input[name="icMode"]').forEach((r) => {
        r.addEventListener('change', () => {
            const target = document.querySelector('input[name="icMode"]:checked').value === 'target';
            $('icGateRow').hidden = target;
            $('icTargetRow').hidden = !target;
        });
    });
    // Quick sizes. Typing "200 КБ" works too, but a tap is what a phone wants.
    document.querySelectorAll('.ic-chip').forEach((chip) => {
        chip.addEventListener('click', () => {
            $('icTargetInput').value = chip.dataset.target;
            document.querySelectorAll('.ic-chip').forEach((c) => c.classList.toggle('active', c === chip));
            clearTargetError();
            markStale();
            TM.send('ic_action', { what: 'target_chip', value: chip.dataset.target });
        });
    });
    $('icTargetInput').addEventListener('input', () => {
        document.querySelectorAll('.ic-chip').forEach((c) => c.classList.remove('active'));
        clearTargetError();
    });

    const rs = $('icResize');
    if (rs) rs.addEventListener('change', () => { $('icResizePxWrap').hidden = rs.value !== 'custom'; });

    $('icCmpSplit').addEventListener('input', drawCompare);
    $('icCmpDiff').addEventListener('click', () => { if (!cmp.diff) TM.send('ic_action', { what: 'diff_map' }); cmp.diff = !cmp.diff; $('icCmpDiff').setAttribute('aria-pressed', cmp.diff); drawCompare(); });
    $('icCmpZoomIn').addEventListener('click', () => { cmp.zoom = Math.min(16, cmp.zoom * 2); drawCompare(); });
    $('icCmpZoomOut').addEventListener('click', () => { cmp.zoom = Math.max(1, cmp.zoom / 2); if (cmp.zoom === 1) { cmp.ox = cmp.oy = 0; } drawCompare(); });
    $('icCmpClose').addEventListener('click', () => $('icCmpDlg').close());
    $('icTuneApply').addEventListener('click', () => applyTune(false));
    $('icTuneReset').addEventListener('click', () => applyTune(true));
    $('icTuneCancel').addEventListener('click', () => $('icTuneDlg').close());

    // Drag to pan while zoomed.
    let drag = null;
    const cv = $('icCmpCanvas');
    cv.addEventListener('pointerdown', (e) => { if (cmp.zoom > 1) { drag = { x: e.clientX, y: e.clientY }; cv.setPointerCapture(e.pointerId); } });
    cv.addEventListener('pointermove', (e) => {
        if (!drag) return;
        cmp.ox += e.clientX - drag.x; cmp.oy += e.clientY - drag.y;
        drag = { x: e.clientX, y: e.clientY };
        drawCompare();
    });
    cv.addEventListener('pointerup', () => { drag = null; });

    // Any change to what the tool was asked for makes the numbers on screen
    // describe a run nobody requested. Say so instead of leaving them there.
    ['icGate', 'icFormat', 'icResize', 'icTrim', 'icMeta', 'icTargetInput', 'icResizePx',
     'icTargetScope', 'icKeepName'].forEach((id) => {
        const el = $(id);
        if (el) el.addEventListener('change', markStale);
    });
    document.querySelectorAll('input[name="icMode"]').forEach((r) => r.addEventListener('change', markStale));

    ['icGate', 'icFormat', 'icResize', 'icTrim', 'icMeta', 'icTargetScope', 'icKeepName'].forEach((id) => {
        const el = $(id);
        if (el && window.__toolSettings) window.__toolSettings.bind(SLUG, el, id, el.type === 'checkbox' ? el.checked : el.value);
    });

    // AVIF is offered only where the browser can really produce it. Measured
    // 2026-08-17: no engine encodes AVIF from canvas, and all three silently
    // return a PNG — so this menu entry stays hidden rather than handing out
    // mislabelled files.
    probeAvif().then((ok) => {
        const opt = $('icFormat') && $('icFormat').querySelector('option[value="avif"]');
        if (opt && !ok) opt.remove();
        TM.send('ic_avif', { available: ok });
    });
    TM.send('ic_open', {
        worker_lane: CAN_WORKER, offscreen: typeof OffscreenCanvas === 'function',
        cores: HW, mem: navigator.deviceMemory || null,
        compression_stream: typeof CompressionStream === 'function',
    });
    render();
}

async function probeAvif() {
    /* Asking CANVAS whether AVIF is possible can only ever answer no — measured
     * on all three engines, canvas.toBlob('image/avif') quietly returns a PNG.
     * The question belongs to the encoder that actually makes AVIF here:
     * WebCodecs AV1. It needs a secure context, which is also why an earlier
     * probe on a data: URL reported the API as missing. */
    try {
        if (typeof VideoEncoder !== 'function') return false;
        const s = await VideoEncoder.isConfigSupported({
            codec: 'av01.0.04M.08', width: 256, height: 256,
            bitrateMode: 'quantizer', latencyMode: 'quality',
        });
        return !!(s && s.supported);
    } catch (e) { return false; }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

})();
